import { Base } from "@/atoms-react/text/Text.react";
import { CompanySituation } from "@/features/admin/types";
import { companyAdminNewListType } from "@/features/company/types";
import { Routes } from "@/features/router";
import { MenuItemWithIcon } from "@/molecules/menu-item-with-icon/menu-item-with-icon";
import { MiniShieldIcon } from "@/molecules/mini-shield-icon/mini-shield-icon";
import { Menu, MenuButton, MenuItems } from "@headlessui/react";
import { BuildingOffice2Icon, EllipsisVerticalIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { twMerge } from "tailwind-merge";

type AdminNewCompaniesTableItemProps = {
  company: companyAdminNewListType;
  index: number;
  handleEdit: (company: companyAdminNewListType) => void;
};

export const AdminNewCompaniesTableItem = (props: AdminNewCompaniesTableItemProps) => {
  const { index, company, handleEdit } = props;

  const { t } = useTranslation(["pass"]);
  const navigate = useNavigate();

  const textClassName = "text-sm lg:text-base truncate w-max-sm ";

  const renderSituation = (situation: string) => {
    const textClassName = "text-sm text-center p-1";

    switch (situation) {
      case CompanySituation.NEW_CLIENT:
        return (
          <div className="w-max max-w-xs rounded-sm border-2 border-[#656565] bg-slate-50 p-1">
            <Base className={textClassName}>{t("admin_page.situation.1")}</Base>
          </div>
        );
      case CompanySituation.OLKYPASS_VERIFICATION:
        return (
          <div className="w-max rounded-sm border-2 border-[#FA8900] bg-[#FDE9CC] p-1">
            <Base className={textClassName}>{t("admin_page.situation.2")}</Base>
          </div>
        );
      case CompanySituation.ALGOREG_VERIFICATION:
        return (
          <div className="w-max rounded-sm border-2 border-[#8B00A18B] bg-[#8B00A11A] p-1">
            <Base className={textClassName}>{t("admin_page.situation.3")}</Base>
          </div>
        );
      case CompanySituation.OPP_VERIFICATION:
        return (
          <div className="w-max rounded-sm border-2 border-[#00AFEB] bg-[#CCEFFB] p-1">
            <Base className={textClassName}>{t("admin_page.situation.4")}</Base>
          </div>
        );
      case CompanySituation.VALIDATED:
        return (
          <div className="w-max rounded-sm border-2 border-[#01B57C] bg-[#CCF0E5] p-1">
            <Base className={textClassName}>{t("admin_page.situation.5")}</Base>
          </div>
        );
      default:
        break;
    }
  };

  const getDateFormatted = (strDate: string) => {
    const date = new Date(strDate);

    return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
  };

  return (
    <li
      className={twMerge(index != 0 && "border-t-2", "flex h-auto min-h-[100px] flex-row p-3")}
      key={index}
    >
      <div className="flex min-w-[100px] basis-2/12 flex-col justify-center">
        <div className="flex flex-row">
          <BuildingOffice2Icon className="mr-1 h-5 w-5 dark:text-white" />
          <div className="max-w-xs pr-6">
            {" "}
            <Base className={textClassName} data-tooltip={company.legal_name}>
              {" "}
              {company.legal_name}
            </Base>
          </div>
        </div>
      </div>

      <div className="flex min-w-[100px] basis-1/12 flex-col justify-center text-center">
        <Base className={textClassName}> {company.rcs}</Base>
      </div>

      <div className="flex min-w-[100px] basis-1/12 flex-col justify-center text-center">
        <Base className={textClassName}>{` ${company.partner}`}</Base>
      </div>

      <div className="flex min-w-[100px] basis-1/12 flex-col items-center justify-center">
        <MiniShieldIcon level={company.olkypass_lvl} />
      </div>

      <div className="flex min-w-[100px] basis-2/12 flex-col items-center justify-center">
        {renderSituation(company.situation)}
      </div>

      <div className="flex min-w-[100px] basis-1/12 flex-col justify-center text-center">
        <Base className={textClassName}>{company.agent_count}</Base>
      </div>

      <div className="flex min-w-[100px] basis-2/12 flex-col justify-center text-center">
        <Base className={textClassName}>{company.bo_count}</Base>
      </div>

      <div className="flex min-w-[100px] basis-1/12 flex-col justify-center text-center">
        <Base className={textClassName}>{getDateFormatted(company.date_updated)}</Base>
      </div>

      <div className="flex min-w-[100px] basis-1/12 flex-col justify-center">
        <div className="flex justify-end">
          <Menu as="div">
            <MenuButton
              data-cy={`menu-button-${company.legal_name}`}
              className="rounded-sm border-2 border-slate bg-white/10 p-2 text-sm hover:border-slate-100 hover:bg-white/20 dark:border-slate-700 dark:border-b-2 dark:bg-slate-950"
            >
              <EllipsisVerticalIcon
                data-cy="menu-button"
                aria-hidden="true"
                className={twMerge("size-5 text-primary")}
              />
            </MenuButton>

            <MenuItems className="z absolute right-0 mt-1 rounded-sm border bg-white py-1">
              <MenuItemWithIcon
                title={t("admin_page.table.menu_button.navigate_links")}
                icon={"Pencil"}
                showBorderBottom
                action={() => navigate(`${Routes.companies}/${company.uuidLegal}/related_person`)}
              />
              <MenuItemWithIcon
                title={t("admin_page.table.menu_button.navigate_olkypass_pro")}
                icon={"Pencil"}
                action={() => navigate(`${Routes.level}/${company.uuidLegal}`)}
                showBorderBottom
              />
              <MenuItemWithIcon
                title={t("admin_page.table.menu_button.update_situation")}
                dataCy={`edit-situation`}
                icon={"Pencil"}
                action={() => handleEdit(company)}
              />
            </MenuItems>
          </Menu>
        </div>
      </div>
    </li>
  );
};
